import { useState } from "react";
import { ApiError } from "@repo/api-client";
import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { canAccessVenue } from "../../shell/roles";
import { formatMoney, type Plan, type Subscription } from "./billingApi";
import { BillingProfileCard } from "./BillingProfileCard";
import {
  useCancelSubscription,
  useChangePlan,
  useOpenBillingPortal,
  usePlanCatalog,
  useReactivateSubscription,
  useStartCheckout,
  useVenueSubscription,
} from "./queries";
import { TrialBanner } from "./TrialBanner";

const card = css({
  border: "1px solid",
  borderColor: "border.default",
  borderRadius: "md",
  padding: "3",
  minWidth: "48",
});
// The current plan gets a thicker branded border so it reads at a glance (the text badge stays too).
const cardCurrent = css({
  colorPalette: "brand",
  borderColor: "colorPalette.default",
  borderWidth: "2px",
});
const cardRow = css({ display: "flex", gap: "3", flexWrap: "wrap", marginBottom: "5" });

const primaryBtn = css({
  bg: "colorPalette.default",
  color: "colorPalette.fg",
  px: "4",
  py: "2",
  borderRadius: "md",
  fontWeight: "semibold",
  fontSize: "sm",
  cursor: "pointer",
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
});
const ghostBtn = css({
  px: "4",
  py: "2",
  borderRadius: "md",
  borderWidth: "1px",
  borderColor: "border.default",
  fontSize: "sm",
  cursor: "pointer",
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
});
const dangerBtn = css({
  px: "4",
  py: "2",
  borderRadius: "md",
  borderWidth: "1px",
  borderColor: "border.default",
  color: "fg.error",
  fontSize: "sm",
  cursor: "pointer",
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
});

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** A period boundary (e.g. the cancel-effective date), localised in the venue's own time zone. */
function formatDate(epochSec: number, lang: string, timeZone: string): string {
  return new Intl.DateTimeFormat(lang, { dateStyle: "long", timeZone }).format(
    new Date(epochSec * 1000),
  );
}

/**
 * The `/v/:venueId/billing` surface (billing-and-plans §C.3, R1) — the single hub for plan management:
 * subscribe / change plan (Stripe prorates: charge now on upgrade, credit next invoice on downgrade),
 * in-app cancel-at-period-end + resume, the current-plan highlight, the billing-identity form, and a
 * link that opens the Stripe Billing Portal (invoices + receipts) in a new tab. Account-root gates the
 * WRITE controls (R1); the server re-enforces (403 for a non-root change).
 */
export function BillingPage() {
  const { venueId, venue } = useVenue();
  const { claims } = useAuth();
  const { t, i18n } = useTranslation();
  const isAccountRoot = claims?.isAccountRoot === true;
  const canView = isAccountRoot || canAccessVenue(claims, venueId, "MANAGER");

  const catalog = usePlanCatalog();
  const subscription = useVenueSubscription(venueId);
  const changePlan = useChangePlan(venueId);
  const startCheckout = useStartCheckout(venueId);
  const cancelSub = useCancelSubscription(venueId);
  const reactivateSub = useReactivateSubscription(venueId);
  const openPortal = useOpenBillingPortal();

  if (!canView) {
    return (
      <PageScaffold title={t("billing.title")}>
        <p>{t("billing.forbidden")}</p>
      </PageScaffold>
    );
  }

  const sub = subscription.data ?? null;
  // A venue not already on a paid ACTIVE subscription signs up via Stripe Checkout (a redirect); once
  // ACTIVE, an entitlement change is the in-DB/proration change-plan path.
  const needsCheckout = sub?.state !== "ACTIVE";
  const currentPriceMinor =
    catalog.data?.plans.find((p) => p.code === sub?.planCode)?.monthlyPrice.amountMinor ?? null;
  const periodEndLabel =
    sub && sub.currentPeriodEndSec !== null
      ? formatDate(sub.currentPeriodEndSec, i18n.language, venue.timeZone)
      : "";

  return (
    <PageScaffold title={t("billing.title")}>
      <TrialBanner />
      <SubscriptionBanner sub={sub} t={t} />

      {isAccountRoot && <BillingProfileCard />}

      <h2 className={css({ fontWeight: "semibold", marginBottom: "2" })}>
        {t("billing.plans.heading")}
      </h2>
      {startCheckout.isError && (
        <p
          role="alert"
          data-testid="billing-checkout-error"
          className={css({ color: "fg.error", marginBottom: "2" })}
        >
          {startCheckout.error instanceof ApiError
            ? startCheckout.error.message
            : t("billing.plans.checkoutFailed")}
        </p>
      )}
      {changePlan.isError && (
        <p
          role="alert"
          data-testid="billing-change-error"
          className={css({ color: "fg.error", marginBottom: "2" })}
        >
          {changePlan.error instanceof ApiError
            ? changePlan.error.message
            : t("billing.plans.changeFailed")}
        </p>
      )}
      {changePlan.isSuccess && (
        <output
          data-testid="billing-change-success"
          className={css({ display: "block", color: "green.text", marginBottom: "2" })}
        >
          {t("billing.plans.changed")}
        </output>
      )}
      {catalog.isLoading ? (
        <p>{t("billing.loading")}</p>
      ) : (
        <div className={cardRow}>
          {(catalog.data?.plans ?? []).map((p) => (
            <PlanCard
              key={p.code}
              plan={p}
              current={sub?.planCode === p.code}
              canEdit={isAccountRoot}
              needsCheckout={needsCheckout}
              currentPriceMinor={currentPriceMinor}
              busy={changePlan.isPending || startCheckout.isPending}
              pending={
                (changePlan.isPending && changePlan.variables?.planCode === p.code) ||
                (startCheckout.isPending && startCheckout.variables?.planCode === p.code)
              }
              onSelect={() =>
                needsCheckout
                  ? startCheckout.mutate({ planCode: p.code, addOnCodes: sub?.addOnCodes ?? [] })
                  : changePlan.mutate({ planCode: p.code, addOnCodes: sub?.addOnCodes ?? [] })
              }
              t={t}
            />
          ))}
        </div>
      )}

      {isAccountRoot && (
        <SubscriptionControls
          sub={sub}
          endLabel={periodEndLabel}
          cancel={cancelSub}
          reactivate={reactivateSub}
          t={t}
        />
      )}

      {isAccountRoot && (
        <section className={css({ marginTop: "5" })}>
          <h2 className={css({ fontWeight: "semibold", marginBottom: "1" })}>
            {t("billing.invoices.heading")}
          </h2>
          <p className={css({ color: "fg.muted", fontSize: "sm", marginBottom: "2" })}>
            {t("billing.portal.hint")}
          </p>
          {openPortal.isError && (
            <p
              role="alert"
              className={css({ color: "fg.error", marginBottom: "2", fontSize: "sm" })}
            >
              {openPortal.error instanceof ApiError
                ? openPortal.error.message
                : t("billing.portal.failed")}
            </p>
          )}
          <button
            type="button"
            disabled={openPortal.isPending}
            // Open the tab SYNCHRONOUSLY inside the gesture (popup blockers), then point it at the
            // portal url once the session resolves; a blocked/null handle falls back inside the hook.
            onClick={() => openPortal.mutate(window.open("about:blank", "_blank"))}
            data-testid="billing-portal-open"
            className={primaryBtn}
          >
            {openPortal.isPending ? t("billing.portal.opening") : t("billing.portal.open")}
          </button>
        </section>
      )}
    </PageScaffold>
  );
}

function SubscriptionBanner({ sub, t }: { sub: Subscription | null; t: Translate }) {
  if (!sub) return null;
  if (sub.state === "SUSPENDED") {
    return (
      <p
        role="alert"
        data-testid="billing-banner-suspended"
        className={css({ color: "fg.error", marginBottom: "3" })}
      >
        {t("billing.banner.suspended")}
      </p>
    );
  }
  if (sub.state === "PAST_DUE") {
    return (
      <p
        role="alert"
        data-testid="billing-banner-pastdue"
        className={css({ color: "fg.warning", marginBottom: "3" })}
      >
        {t("billing.banner.pastDue")}
      </p>
    );
  }
  return (
    <p className={css({ color: "fg.muted", marginBottom: "3" })} data-testid="billing-state">
      {t("billing.state", { state: t(`billing.states.${sub.state}`) })}
    </p>
  );
}

type Mutation = { mutate: () => void; isPending: boolean };

/**
 * The cancel / resume controls (account-root). When a cancel is scheduled the subscription stays ACTIVE
 * with access until [endLabel], showing a "resume" button; otherwise an ACTIVE subscription shows an
 * inline-confirmed "cancel" (no blocking window.confirm, consistent with the rest of the app).
 */
function SubscriptionControls({
  sub,
  endLabel,
  cancel,
  reactivate,
  t,
}: {
  sub: Subscription | null;
  endLabel: string;
  cancel: Mutation;
  reactivate: Mutation;
  t: Translate;
}) {
  const [armed, setArmed] = useState(false);
  if (!sub) return null;

  if (sub.cancelAtPeriodEnd) {
    return (
      <section
        data-testid="billing-cancel-scheduled"
        className={css({
          marginBottom: "5",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "3",
        })}
      >
        <p className={css({ color: "fg.warning", fontSize: "sm" })}>
          {t("billing.plans.activeUntil", { date: endLabel })}
        </p>
        <button
          type="button"
          className={primaryBtn}
          data-testid="billing-reactivate"
          disabled={reactivate.isPending}
          onClick={() => reactivate.mutate()}
        >
          {reactivate.isPending ? t("billing.plans.reactivating") : t("billing.plans.reactivate")}
        </button>
      </section>
    );
  }

  if (sub.state !== "ACTIVE") return null;

  return (
    <section className={css({ marginBottom: "5" })}>
      {armed ? (
        <div className={css({ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "3" })}>
          <span className={css({ fontSize: "sm" })}>
            {t("billing.plans.cancelConfirm", { date: endLabel })}
          </span>
          <button
            type="button"
            className={dangerBtn}
            data-testid="billing-cancel-confirm"
            disabled={cancel.isPending}
            onClick={() => {
              setArmed(false);
              cancel.mutate();
            }}
          >
            {cancel.isPending ? t("billing.plans.cancelling") : t("billing.plans.confirm")}
          </button>
          <button type="button" className={ghostBtn} onClick={() => setArmed(false)}>
            {t("billing.plans.keep")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={dangerBtn}
          data-testid="billing-cancel"
          onClick={() => setArmed(true)}
        >
          {t("billing.plans.cancel")}
        </button>
      )}
    </section>
  );
}

/** What each plan actually includes — the limits line plus the included capability flags in plain
 *  language, so a restaurateur can compare tiers on features, not just seat/venue caps. */
function PlanFeatures({ plan, t }: { plan: Plan; t: Translate }) {
  const gb = plan.mediaQuotaBytes / 1_000_000_000;
  const flagLabel = (flag: string) =>
    flag === "SMS" && plan.smsCredits > 0
      ? t("billing.feature.smsCredits", { count: plan.smsCredits })
      : t(`billing.flag.${flag}`, { defaultValue: flag });
  return (
    <ul
      className={css({
        listStyle: "none",
        p: "0",
        m: "0",
        mt: "2",
        display: "flex",
        flexDirection: "column",
        gap: "1",
        fontSize: "xs",
        color: "fg.muted",
      })}
    >
      <li>{t("billing.plans.limits", { venues: plan.maxVenues, seats: plan.staffSeats })}</li>
      {plan.includedFlags
        .filter((flag) => flag !== "MEDIA")
        .map((flag) => (
          <li key={flag}>✓ {flagLabel(flag)}</li>
        ))}
      {plan.mediaQuotaBytes > 0 && (
        <li>✓ {t("billing.feature.media", { gb: gb >= 1 ? gb.toFixed(0) : gb.toFixed(1) })}</li>
      )}
    </ul>
  );
}

function PlanCard({
  plan,
  current,
  canEdit,
  needsCheckout,
  currentPriceMinor,
  busy,
  pending,
  onSelect,
  t,
}: {
  plan: Plan;
  current: boolean;
  canEdit: boolean;
  needsCheckout: boolean;
  currentPriceMinor: number | null;
  busy: boolean;
  pending: boolean;
  onSelect: () => void;
  t: Translate;
}) {
  const [armed, setArmed] = useState(false);
  // Direction drives the proration copy + the button label; only meaningful for an in-place change.
  const isUpgrade = currentPriceMinor !== null && plan.monthlyPrice.amountMinor > currentPriceMinor;

  return (
    <div
      className={current ? `${card} ${cardCurrent}` : card}
      data-testid={`plan-${plan.code}`}
      data-current={current}
    >
      <strong>{plan.name}</strong>
      <p>{t("billing.plans.perMonth", { price: formatMoney(plan.monthlyPrice) })}</p>
      <PlanFeatures plan={plan} t={t} />
      {current ? (
        <span
          data-testid={`plan-${plan.code}-current`}
          className={css({ fontSize: "xs", fontWeight: "semibold", color: "colorPalette.text" })}
        >
          {t("billing.plans.current")}
        </span>
      ) : !canEdit ? null : needsCheckout ? (
        // Not ACTIVE yet → start Stripe Checkout (a redirect). Kept mounted (disabled) while in flight.
        <button type="button" className={ghostBtn} onClick={onSelect} disabled={busy}>
          {pending ? t("billing.plans.redirecting") : t("billing.plans.subscribe")}
        </button>
      ) : armed ? (
        // In-place change → confirm with the generic proration note (charge now up / credit next down).
        <div className={css({ display: "flex", flexDirection: "column", gap: "1", mt: "2" })}>
          <span className={css({ fontSize: "xs", color: "fg.muted" })}>
            {isUpgrade
              ? t("billing.plans.prorationUpgrade")
              : t("billing.plans.prorationDowngrade")}
          </span>
          <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
            <button
              type="button"
              className={primaryBtn}
              disabled={busy}
              onClick={() => {
                setArmed(false);
                onSelect();
              }}
            >
              {pending ? t("billing.plans.choosing") : t("billing.plans.confirm")}
            </button>
            <button type="button" className={ghostBtn} onClick={() => setArmed(false)}>
              {t("billing.plans.back")}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className={ghostBtn} onClick={() => setArmed(true)} disabled={busy}>
          {isUpgrade ? t("billing.plans.upgrade") : t("billing.plans.downgrade")}
        </button>
      )}
    </div>
  );
}
